import { MenuItem, PropertyValue, type RadioItem } from '@affine/component';
import type { FilterParams } from '@affine/core/modules/collection-rules';
import { type DocRecord, DocService } from '@affine/core/modules/doc';
import { useI18n } from '@affine/i18n';
import { EdgelessIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { PlainTextDocGroupHeader } from '../explorer/docs-view/group-header';
import { StackProperty } from '../explorer/docs-view/stack-property';
import type { GroupHeaderProps } from '../explorer/types';
import { FilterValueMenu } from '../filter/filter-value-menu';
import type { PropertyValueProps } from '../properties/types';
import { PropertyRadioGroup } from '../properties/widgets/radio-group';
import * as styles from './edgeless-theme.css';

const getThemeOptions = (t: ReturnType<typeof useI18n>) =>
  [
    {
      value: 'system',
      label: t['com.streamlain.themeSettings.auto'](),
    },
    {
      value: 'light',
      label: t['com.streamlain.themeSettings.light'](),
    },
    {
      value: 'dark',
      label: t['com.streamlain.themeSettings.dark'](),
    },
    // E016-S003: Blue/White canvas option
    {
      value: 'blue-white',
      label: t['com.streamlain.themeSettings.blue'](),
    },
  ] satisfies RadioItem[];

export const EdgelessThemeValue = ({
  onChange,
  readonly,
}: PropertyValueProps) => {
  const t = useI18n();
  const doc = useService(DocService).doc;
  const edgelessTheme = useLiveData(doc.properties$).edgelessColorTheme;

  const handleChange = useCallback(
    (theme: string) => {
      doc.record.setProperty('edgelessColorTheme', theme);
      onChange?.(theme, true);
    },
    [doc, onChange]
  );
  const themeItems = useMemo<RadioItem[]>(() => getThemeOptions(t), [t]);

  return (
    <PropertyValue
      className={styles.container}
      hoverable={false}
      readonly={readonly}
    >
      <PropertyRadioGroup
        value={edgelessTheme || 'system'}
        onChange={handleChange}
        items={themeItems}
        disabled={readonly}
        width="100%"
      />
    </PropertyValue>
  );
};

export const EdgelessThemeDocListProperty = ({ doc }: { doc: DocRecord }) => {
  const t = useI18n();
  const edgelessTheme = useLiveData(
    doc.properties$.selector(p => p.edgelessColorTheme)
  );

  // E016-S003: Helper to get display label for theme value
  const getThemeLabel = (theme: string | null | undefined) => {
    if (!theme || theme === 'system')
      return t['com.streamlain.themeSettings.auto']();
    if (theme === 'light') return t['com.streamlain.themeSettings.light']();
    if (theme === 'dark') return t['com.streamlain.themeSettings.dark']();
    if (theme === 'blue-white') return t['com.streamlain.themeSettings.blue']();
    return theme;
  };

  return (
    <StackProperty icon={<EdgelessIcon />}>
      {getThemeLabel(edgelessTheme)}
    </StackProperty>
  );
};

export const EdgelessThemeFilterValue = ({
  filter,
  isDraft,
  onDraftCompleted,
  onChange,
}: {
  filter: FilterParams;
  isDraft?: boolean;
  onDraftCompleted?: () => void;
  onChange?: (filter: FilterParams) => void;
}) => {
  const t = useI18n();

  // E016-S003: Helper to get display label for theme value
  const getFilterLabel = (value: unknown) => {
    if (value === 'system') return t['com.streamlain.themeSettings.auto']();
    if (value === 'light') return t['com.streamlain.themeSettings.light']();
    if (value === 'dark') return t['com.streamlain.themeSettings.dark']();
    if (value === 'blue-white') return t['com.streamlain.themeSettings.blue']();
    return String(value);
  };

  return (
    <FilterValueMenu
      isDraft={isDraft}
      onDraftCompleted={onDraftCompleted}
      items={
        <>
          <MenuItem
            onClick={() => {
              onChange?.({
                ...filter,
                value: 'system',
              });
            }}
            selected={filter.value === 'system'}
          >
            {t['com.streamlain.themeSettings.auto']()}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onChange?.({
                ...filter,
                value: 'light',
              });
            }}
            selected={filter.value === 'light'}
          >
            {t['com.streamlain.themeSettings.light']()}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onChange?.({
                ...filter,
                value: 'dark',
              });
            }}
            selected={filter.value === 'dark'}
          >
            {t['com.streamlain.themeSettings.dark']()}
          </MenuItem>
          {/* E016-S003: Blue/White filter option */}
          <MenuItem
            onClick={() => {
              onChange?.({
                ...filter,
                value: 'blue-white',
              });
            }}
            selected={filter.value === 'blue-white'}
          >
            {t['com.streamlain.themeSettings.blue']()}
          </MenuItem>
        </>
      }
    >
      <span>{getFilterLabel(filter.value)}</span>
    </FilterValueMenu>
  );
};

export const EdgelessThemeGroupHeader = ({
  groupId,
  docCount,
}: GroupHeaderProps) => {
  const t = useI18n();
  // E016-S003: Added blue-white option
  const text =
    groupId === 'light'
      ? t['com.streamlain.themeSettings.light']()
      : groupId === 'dark'
        ? t['com.streamlain.themeSettings.dark']()
        : groupId === 'system'
          ? t['com.streamlain.themeSettings.auto']()
          : groupId === 'blue-white'
            ? t['com.streamlain.themeSettings.blue']()
            : 'Default';

  return (
    <PlainTextDocGroupHeader groupId={groupId} docCount={docCount}>
      {text}
    </PlainTextDocGroupHeader>
  );
};
